import { default as _Vue } from 'vue';
import { Store } from 'vuex';
import { AbstractService } from '@/plugins/podolib/lib/AbstractService';
import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import { Doctor } from '@/plugins/podolib/services/doctor';

// Service options type
export type TOptions = {
  key: string;
};

interface Analytics {
  [key: string]: any;

  initialize?: () => void;
  invoked?: boolean;
  methods: string[];
}

// SDK's methods available in service
enum MethodsSDK {
  // TrackSubmit = 'trackSubmit', // Not allowed
  TrackClick = 'trackClick',
  TrackLink = 'trackLink',
  // TrackForm = 'trackForm', // Not allowed
  Pageview = 'pageview',
  Identify = 'identify',
  Reset = 'reset',
  Group = 'group',
  // Track = 'track', // Please use helpers instead
  Ready = 'ready',
  Alias = 'alias',
  Debug = 'debug',
  Page = 'page',
  Once = 'once',
  Off = 'off',
  On = 'on',
  User = 'user',
}

enum MethodsHelpers {
  TrackSuccess = 'trackSuccess',
  TrackFailure = 'trackFailure',
}

type MethodsKeys = {
  [key in MethodsSDK]: (...args: any) => void;
};

type MethodsKeysExtended = MethodsKeys &
  {
    [key in MethodsHelpers]: (type: string, data?: any) => void;
  };

type Methods = MethodsKeysExtended & {
  trackSuccess: (type: string, options?: any, previous?: any) => void;
  trackFailure: (type: string, error: Error, options?: any, previous?: any) => void;
};

export enum StateStatus {
  Wait,
  Loaded,
  Failed,
}

export type State = {
  status: StateStatus;
};

/**
 * Analytics service
 *
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class AnalyticsService extends AbstractService<TOptions, any> {
  // Loaded flag
  public actions = {} as Methods;

  /**
   * @param options
   */
  constructor(options: TOptions) {
    super(options, 'analytics');

    if (!options.key) {
      this.logger.fatal('Missing key');
      return;
    }

    // Add temporary decoys
    this.createDecoys();
  }

  /**
   * Initialize plugin
   */
  async init(Vue: typeof _Vue, store: Store<any>) {
    await super.init(Vue, store);

    this.$register(this.defaultState, {
      mutations: {
        SET_STATUS(state, status: StateStatus) {
          state.status = status;
        },
      },
    });

    this.loadScript(this.options.key)
      .then(() => {
        if (this.$state('status') === StateStatus.Failed) {
          this.logger.warn('🛂 Failed to load in allowed time');
          return;
        }

        // Add interceptors
        this.createInterceptors();

        this.logger.success('🤘 Ready');
        this.$commit('SET_STATUS', StateStatus.Loaded);
        // Send event
        this.Vue.prototype.$pdc.bus.emitDebug('analytics:loaded');
      })
      .catch(() => {
        this.logger.warn('❌ Failed to load');
        this.$commit('SET_STATUS', StateStatus.Failed);
        // Send event
        this.Vue.prototype.$pdc.bus.emitDebug('analytics:timeout');
      });

    this.startListener();
  }

  startListener() {
    this.Vue.prototype.$pdc.bus.on('analytics:middleware-timeout', () => {
      this.$commit('SET_STATUS', StateStatus.Failed);
    });
  }

  /**
   * Get default state of this store module
   */
  public get defaultState(): State {
    return {
      ...super.defaultState,
      status: StateStatus.Wait,
    };
  }

  loadScript(key: string) {
    return new Promise((resolve, reject) => {
      reject('Deactivation fixed');
      return;
      // Segment SDK
      const source = `https://cdn.segment.com/analytics.js/v1/${key}/analytics.min.js`;
      // Create script element
      const n = document.createElement('script');
      n.type = 'text/javascript';
      n.src = source;
      // Timout
      const _t = setTimeout(() => reject('Timeout'), 5000);
      // Observe onload event
      n.onload = () => {
        clearTimeout(_t);
        resolve();
      };
      // Inject in document's head
      document.head.appendChild(n);
    });
  }

  /**
   * Temporary decoys, to avoid error if interceptors haven't been set
   */
  createDecoys() {
    [...Object.values(MethodsSDK), ...Object.values(MethodsHelpers)].forEach((key) => {
      this.actions[key] = () => {
        if (this.status === StateStatus.Wait) {
          console.warn(`Event "${key}" not available`);
        }
      };
    });
  }

  /**
   * Interceptors
   */
  createInterceptors() {
    Object.values(MethodsSDK).forEach((key) => {
      this.actions[key] = function () {
        return (window as any).analytics[key].apply(this, arguments);
      };
    });

    this.actions.trackSuccess = (type: string, data?: any, previous?: any) => {
      return (window as any).analytics.track.apply(this, [
        type,
        {
          status: 'success',
          error: null,
          data,
          previous,
        },
      ]);
    };

    this.actions.trackFailure = (type: string, error: Error, data?: any, previous?: any) => {
      return (window as any).analytics.track.apply(this, [
        type,
        {
          status: 'error',
          error: error instanceof Error ? error.message || error.stack : error,
          data,
          previous,
        },
      ]);
    };
  }

  get status() {
    return this.$state('status');
  }
}
